import { useEffect, useMemo, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { ArrowRight, Ban, CheckCircle2, Database, GitBranch, Inbox, Plus, RotateCcw, Search, ShieldCheck, Trash2 } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useCreateDataAsset, useListDataAssets, useDataAssetLifecycle, getListDataAssetsQueryKey } from "@tubo/api-models/hooks/data-asset-api.hooks";
import { useListModelVersions, useSemanticDataModelList } from "@tubo/api-models/hooks/semantic-data-model-api.hooks";
import { Button } from "@tubo/ui/components/ui/button";
import { Badge } from "@tubo/ui/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@tubo/ui/components/ui/dialog";
import { Input } from "@tubo/ui/components/ui/input";
import { Label } from "@tubo/ui/components/ui/label";
import { DataTable, type DataTableColumn } from "@tubo/ui/components/ui/data-table";
import {
  useSecondaryNavActions,
  useSecondaryNavTitle,
} from "@client/components/secondary-nav";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@tubo/ui/components/ui/select";
import { DataAssetStatusEnum } from "@tubo/api-models/business/data-asset/data-asset.enums";
import type { AssetListItemResp } from "@tubo/api-models/hooks/data-asset-api.schemas";
import { DeleteAssetDialog } from "@client/components/semantic-platform/data-assets/delete-asset-dialog";
import { useNsContext } from "@client/components/semantic-platform/namespace-context";
import { useUiTranslation } from "@tubo/ui/i18n";

export const Route = createFileRoute("/_app/semantic-platform/data-assets/")({
  // 领域上下文入 URL（缺省=全局；__all__=全部领域聚合浏览；经 modelId 服务端派生过滤）
  validateSearch: (search: Record<string, unknown>): { ns?: string } => {
    return typeof search.ns === "string" && search.ns ? { ns: search.ns } : {};
  },
  component: DataAssetListPage,
});

const PAGE_SIZE = 20;

const STATUS_LABEL_KEY: Record<string, string> = {
  [DataAssetStatusEnum.Draft]: "draft",
  [DataAssetStatusEnum.Building]: "building",
  [DataAssetStatusEnum.Publishable]: "publishable",
  [DataAssetStatusEnum.Published]: "published",
  [DataAssetStatusEnum.Suspended]: "suspended",
  [DataAssetStatusEnum.Archived]: "archived",
};

const MODEL_STATUS_LABEL_KEY: Record<string, string> = {
  draft: "draft",
  pendingReview: "pendingReview",
  published: "published",
  deprecated: "deprecated",
};

const ASSET_LIFECYCLE_STEPS = [
  { key: "step1", icon: GitBranch },
  { key: "step2", icon: Inbox },
  { key: "step3", icon: ShieldCheck },
  { key: "step4", icon: CheckCircle2 },
  { key: "step5", icon: Database },
] as const;

function DataAssetListPage() {
  const { t, i18n } = useUiTranslation("semanticPlatform");
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [status, setStatus] = useState<string>("all");
  const [keyword, setKeyword] = useState("");
  const [deleteTarget, setDeleteTarget] = useState<AssetListItemResp | null>(null);
  const statusValues = Object.values(DataAssetStatusEnum);
  const selectedStatus = statusValues.find((v) => v === status);
  const statusFilterOptions = useMemo(
    () => [
      { value: "all", label: t("dataAssets.list.allStatuses") },
      ...Object.entries(STATUS_LABEL_KEY).map(([value, key]) => ({
        value,
        label: t("dataAssets.list.statusFilter", { status: t(`dataAssets.status.${key}`) }),
      })),
    ],
    [t],
  );
  // 领域上下文（服务端派生过滤：经 modelId→模型→namespaceId）
  const { ns, nsFilter } = useNsContext();

  const { data, isLoading } = useListDataAssets(
    {
      page,
      pageSize: pageSize,
      ...(selectedStatus ? { status: selectedStatus } : {}),
      ...(keyword.trim() ? { q: keyword.trim() } : {}),
      ns: nsFilter,
    },
    { query: { placeholderData: (prev) => prev } },
  );

  const lifecycle = useDataAssetLifecycle({
    mutation: {
      onSuccess: async () => {
        toast.success(t("dataAssets.list.lifecycleSucceeded"));
        await queryClient.invalidateQueries({ queryKey: getListDataAssetsQueryKey() });
      },
      onError: (err) => toast.error(err instanceof Error ? err.message : t("common.operationFailed")),
    },
  });

  const assets = data?.items ?? [];
  const total = data?.total ?? 0;

  const columns = useMemo<DataTableColumn<AssetListItemResp>[]>(
    () => [
      { id: "code", header: t("dataAssets.list.code"), cell: (row) => <span className="font-mono text-xs">{row.code}</span> },
      { id: "name", header: t("dataAssets.list.name"), cell: (row) => row.name },
      {
        id: "status",
        header: t("dataAssets.list.status"),
        cell: (row) => (
          <Badge variant={row.status === DataAssetStatusEnum.Published ? "default" : "secondary"}>
            {STATUS_LABEL_KEY[row.status] ? t(`dataAssets.status.${STATUS_LABEL_KEY[row.status]}`) : row.status}
          </Badge>
        ),
      },
      {
        id: "namespace",
        header: t("dataAssets.list.domain"),
        cell: (row) =>
          row.namespaceId ? (
            <Badge variant="outline">{row.namespaceName ?? row.namespaceId}</Badge>
          ) : (
            <span className="text-xs text-muted-foreground">{t("dataAssets.list.global")}</span>
          ),
      },
      { id: "modelVersion", header: t("dataAssets.list.modelVersion"), cell: (row) => <span className="text-xs text-muted-foreground">{row.modelVersion}</span> },
      { id: "createdAt", header: t("dataAssets.list.createdAt"), cell: (row) => <span className="text-xs text-muted-foreground">{new Date(row.createdAt).toLocaleString(i18n.resolvedLanguage)}</span> },
      {
        id: "actions",
        header: t("dataAssets.list.actions"),
        cell: (row) => (
          <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
            {row.status === DataAssetStatusEnum.Published && (
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label={t("dataAssets.list.suspend")}
                title={t("dataAssets.list.suspend")}
                disabled={lifecycle.isPending}
                onClick={() => lifecycle.mutate({ id: row.id, data: { action: "suspend", reason: "列表页停用" } })}
              >
                <Ban className="size-3.5" />
              </Button>
            )}
            {row.status === DataAssetStatusEnum.Suspended && (
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label={t("dataAssets.list.restore")}
                title={t("dataAssets.list.restore")}
                disabled={lifecycle.isPending}
                onClick={() => lifecycle.mutate({ id: row.id, data: { action: "restore" } })}
              >
                <RotateCcw className="size-3.5" />
              </Button>
            )}
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label={t("common.delete")}
              title={t("dataAssets.list.deleteToRecycleBin")}
              className="text-destructive hover:text-destructive"
              onClick={() => setDeleteTarget(row)}
            >
              <Trash2 className="size-3.5" />
            </Button>
          </div>
        ),
      },
    ],
    [i18n.resolvedLanguage, lifecycle, t],
  );

  const headerTitle = useMemo(
    () => <span className="text-sm font-semibold">{t("navigation.dataAssets")}</span>,
    [t],
  );
  useSecondaryNavTitle(headerTitle);

  const headerActions = useMemo(
    () => (
      <div className="flex items-center gap-2">
        <Select items={statusFilterOptions} value={status} onValueChange={(value) => { setStatus(value ?? "all"); setPage(1); }}>
          <SelectTrigger className="h-8 min-w-28 max-w-48 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {statusFilterOptions.map((option) => (
              <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="relative">
          <Search className="absolute left-2 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            className="w-56 pl-8"
            placeholder={t("dataAssets.list.searchPlaceholder")}
            value={keyword}
            onChange={(event) => { setKeyword(event.target.value); setPage(1); }}
          />
        </div>
        <Button size="sm" onClick={() => setCreateOpen(true)}>
          <Plus className="mr-1 size-4" /> {t("dataAssets.list.create")}
        </Button>
      </div>
    ),
    [keyword, status, statusFilterOptions, t],
  );
  useSecondaryNavActions(headerActions);

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col">
      <AssetLifecycleGuide />
      <div className="min-h-0 flex-1">
        <DataTable
          columns={columns}
          data={assets}
          rowKey={(row) => row.id}
          page={page}
          pageSize={pageSize}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPage(1);
          }}
          total={total}
          onPageChange={setPage}
          isLoading={isLoading}
          emptyState={
            <div className="flex h-64 flex-col items-center justify-center text-muted-foreground">
              <Database className="mb-3 h-12 w-12 text-icon-secondary-muted" />
              <p className="text-sm font-medium">{t("dataAssets.list.emptyTitle")}</p>
              <p className="mt-1 text-xs">{t("dataAssets.list.emptyDescription")}</p>
            </div>
          }
          onRowClick={(row) =>
            navigate({ to: "/semantic-platform/data-assets/$assetId", params: { assetId: row.id }, search: ns ? { ns } : {} })
          }
        />
      </div>

      <CreateAssetDialog open={createOpen} onOpenChange={setCreateOpen} />

      {deleteTarget && (
        <DeleteAssetDialog
          open
          onOpenChange={(next) => {
            if (!next) setDeleteTarget(null);
          }}
          id={deleteTarget.id}
          name={deleteTarget.name}
        />
      )}
    </div>
  );
}

function AssetLifecycleGuide() {
  const { t } = useUiTranslation("semanticPlatform");
  return (
    <section className="shrink-0 border-b bg-muted/15 px-4 py-3">
      <div className="flex flex-col gap-3 rounded-lg border bg-background px-4 py-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h2 className="text-sm font-semibold">{t("dataAssets.list.guideTitle")}</h2>
            <p className="mt-0.5 text-xs text-muted-foreground">
              {t("dataAssets.list.guideDescription")}
            </p>
          </div>
        </div>
        <div className="grid gap-3 md:grid-cols-5">
          {ASSET_LIFECYCLE_STEPS.map((step, index) => {
            const Icon = step.icon;
            return (
              <div key={step.key} className="relative flex gap-2.5 md:pr-3">
                <div className="flex size-7 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                  <Icon className="size-3.5" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-medium">{t(`dataAssets.list.${step.key}`)}</p>
                  <p className="mt-0.5 text-[11px] leading-4 text-muted-foreground">{t(`dataAssets.list.${step.key}Description`)}</p>
                </div>
                {index < ASSET_LIFECYCLE_STEPS.length - 1 && (
                  <ArrowRight className="absolute right-0 top-1.5 hidden size-3.5 text-muted-foreground/50 md:block" />
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function CreateAssetDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (v: boolean) => void }) {
  const { t, i18n } = useUiTranslation("semanticPlatform");
  const navigate = useNavigate();
  const { ns } = useNsContext();
  const queryClient = useQueryClient();
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [modelId, setModelId] = useState("");
  const [modelVersion, setModelVersion] = useState("");

  const { data: models, isLoading: modelsLoading } = useSemanticDataModelList(undefined, {
    query: { enabled: open },
  });
  const { data: modelVersions, isLoading: versionsLoading } = useListModelVersions(modelId, {
    query: { enabled: open && Boolean(modelId) },
  });
  const selectedModel = models?.find((model) => model.id === modelId);
  const selectedVersion = modelVersions?.find((version) => version.version === modelVersion);
  const modelItems = useMemo(
    () => (models ?? []).map((model) => ({ value: model.id, label: `${model.name} · ${model.code}` })),
    [models],
  );
  const versionItems = useMemo(
    () => (modelVersions ?? []).map((version) => ({ value: version.version, label: `${version.version} · ${version.name}` })),
    [modelVersions],
  );

  useEffect(() => {
    if (!modelId || !modelVersions?.length) return;
    if (!modelVersions.some((version) => version.version === modelVersion)) {
      setModelVersion(modelVersions[0].version);
    }
  }, [modelId, modelVersion, modelVersions]);

  const resetForm = () => {
    setCode("");
    setName("");
    setModelId("");
    setModelVersion("");
  };

  const createAsset = useCreateDataAsset({
    mutation: {
      onSuccess: async (resp) => {
        toast.success(t("dataAssets.list.created"));
        await queryClient.invalidateQueries({ queryKey: getListDataAssetsQueryKey() });
        resetForm();
        onOpenChange(false);
        navigate({ to: "/semantic-platform/data-assets/$assetId", params: { assetId: resp.id }, search: ns ? { ns } : {} });
      },
      onError: (err) => {
        toast.error(err instanceof Error ? err.message : t("dataAssets.list.createFailed"));
      },
    },
  });

  const valid = Boolean(code.trim() && name.trim() && modelId && modelVersion);

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) resetForm();
    onOpenChange(nextOpen);
  };

  const submit = () => {
    if (!valid) {
      toast.error(t("dataAssets.list.requiredFields"));
      return;
    }
    createAsset.mutate({ data: { code: code.trim(), name: name.trim(), modelId, modelVersion } });
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t("dataAssets.list.createTitle")}</DialogTitle>
          <DialogDescription>
            {t("dataAssets.list.createDescription")}
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-3">
          <div className="grid gap-1.5">
            <Label>{t("dataAssets.list.assetCode")}</Label>
            <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder={t("dataAssets.list.assetCodePlaceholder")} />
          </div>
          <div className="grid gap-1.5">
            <Label>{t("dataAssets.list.assetName")}</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={t("dataAssets.list.assetNamePlaceholder")} />
          </div>
          <div className="grid gap-1.5">
            <Label>{t("dataAssets.list.scenarioModel")}</Label>
            <Select
              items={modelItems}
              value={modelId}
              onValueChange={(value) => {
                setModelId(value ?? "");
                setModelVersion("");
              }}
              disabled={modelsLoading || modelItems.length === 0}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder={modelsLoading ? t("dataAssets.list.loadingModels") : t("dataAssets.list.chooseModel")} />
              </SelectTrigger>
              <SelectContent>
                {(models ?? []).map((model) => (
                  <SelectItem key={model.id} value={model.id}>
                    <span>{model.name}</span>
                    <span className="text-xs text-muted-foreground">{model.code} · {MODEL_STATUS_LABEL_KEY[model.status] ? t(`dataAssets.status.${MODEL_STATUS_LABEL_KEY[model.status]}`) : model.status}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {!modelsLoading && modelItems.length === 0 && (
              <p className="text-[11px] text-muted-foreground">
                {t("dataAssets.list.noModels")}
                <Link to="/semantic-platform/data-models" className="ml-1 text-primary hover:underline">{t("dataAssets.list.goToModels")}</Link>
              </p>
            )}
            {selectedModel && (
              <p className="text-[11px] text-muted-foreground">
                {t("dataAssets.list.modelSelected", { name: selectedModel.name, code: selectedModel.code })}
              </p>
            )}
          </div>
          <div className="grid gap-1.5">
            <Label>{t("dataAssets.list.modelVersionRequired")}</Label>
            <Select
              items={versionItems}
              value={modelVersion}
              onValueChange={(value) => setModelVersion(value ?? "")}
              disabled={!modelId || versionsLoading || versionItems.length === 0}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder={versionsLoading ? t("dataAssets.list.loadingVersions") : t("dataAssets.list.chooseVersion")} />
              </SelectTrigger>
              <SelectContent>
                {(modelVersions ?? []).map((version) => (
                  <SelectItem key={version.id} value={version.version}>
                    <span>{version.version} · {version.name}</span>
                    <span className="text-xs text-muted-foreground">{version.publishStatus === "published" ? t("dataAssets.status.published") : version.publishStatus === "pendingReview" ? t("dataAssets.status.pendingReview") : t("dataAssets.status.saved")}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {modelId && !versionsLoading && versionItems.length === 0 && (
              <p className="text-[11px] text-muted-foreground">
                {t("dataAssets.list.noVersions")}
                <Link to="/semantic-platform/data-models/$id" params={{ id: modelId }} className="ml-1 text-primary hover:underline">{t("dataAssets.list.goToModel")}</Link>
              </p>
            )}
            {selectedVersion && (
              <p className="text-[11px] text-muted-foreground">
                {t("dataAssets.list.snapshotNote", { date: new Date(selectedVersion.frozenAt).toLocaleString(i18n.resolvedLanguage) })}
              </p>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)}>{t("dataAssets.list.cancel")}</Button>
          <Button onClick={submit} disabled={!valid || createAsset.isPending}>{t("dataAssets.list.submit")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const STATUS_LABEL: Record<string, string> = {
  [DataAssetStatusEnum.Draft]: "草稿",
  [DataAssetStatusEnum.Building]: "建设中",
  [DataAssetStatusEnum.Publishable]: "可发布",
  [DataAssetStatusEnum.Published]: "已发布",
  [DataAssetStatusEnum.Suspended]: "已停用",
  [DataAssetStatusEnum.Archived]: "已归档",
};

export { STATUS_LABEL };
